import Dexie from 'dexie';

// Initialize Dexie database
export const db = new Dexie('nis');

// Define database tables and index keys
db.version(1).stores({
  area: 'id',
  zone: 'id, area_id',
  image: '++id',
  trial: 'id, area_id',
  trialDetails: 'id, trial_id, zone_id, img_id',
  drafts: 'formId'
});

/**
 * Saves inspection form inputs and an image file offline in a single transaction.
 * Also saves custom zones if they are new.
 */
export const saveInspectionOffline = async (formData, imageFile, customZone = null) => {
  return db.transaction('rw', [db.image, db.trial, db.trialDetails, db.zone], async () => {
    
    // 1. Save custom zone if it's a new one
    if (customZone) {
      await db.zone.put({
        id: customZone.id,
        zone: customZone.zone,
        area_id: customZone.area_id,
        created_at: customZone.created_at
      });
    }

    // 2. Save the binary image file
    let imageId = null;
    if (imageFile) {
      imageId = await db.image.add({
        name: imageFile.name,
        type: imageFile.type,
        size: imageFile.size,
        data: await imageFile.arrayBuffer() // Raw array buffer for browser storage
      });
    } else if (formData.img_id) {
      imageId = formData.img_id;
    }

    // 3. Save the trial header record
    await db.trial.put({
      id: formData.trial_id,
      area_id: formData.area_id,
      created_at: new Date()
    });

    // 4. Save the trial details record
    await db.trialDetails.put({
      id: formData.id,
      trial_id: formData.trial_id,
      zone_id: formData.zone_id,
      img_id: imageId,
      xrfNumber: formData.xrfNumber,
      Ni: formData.Ni,
      remarksValue: formData.remarksValue,
      comments: formData.comments,
      date: formData.date,
      name: formData.name
    });
  });
};

/**
 * Fetches and joins local offline tables for display or sync.
 */
export const getJoinedOfflineInspections = async () => {
  const trials = await db.trial.toArray();
  const details = await db.trialDetails.toArray();
  const areas = await db.area.toArray();
  const zones = await db.zone.toArray();
  const images = await db.image.toArray();

  return details.map(detail => {
    const trial = trials.find(t => t.id === detail.trial_id);
    if (!trial) return null;

    const area = areas.find(a => a.id === trial.area_id);
    const zone = zones.find(z => z.id === detail.zone_id);
    const image = images.find(img => img.id === detail.img_id);

    return {
      id: detail.id,
      trial_id: detail.trial_id,
      area_id: trial.area_id,
      zone_id: detail.zone_id,
      img_id: detail.img_id,
      xrfNumber: detail.xrfNumber,
      Ni: detail.Ni,
      remarksValue: detail.remarksValue,
      comments: detail.comments,
      date: detail.date,
      name: detail.name,
      area: area || null,
      zone: zone || null,
      image: image || null
    };
  }).filter(item => item !== null);
};

/**
 * Deletes a single completed inspection set from local Dexie DB.
 */
export const deleteSyncedInspection = async (trialDetailsId, trialId, imgId) => {
  return db.transaction('rw', [db.trialDetails, db.trial, db.image], async () => {
    await db.trialDetails.delete(trialDetailsId);
    if (trialId) {
      await db.trial.delete(trialId);
    }
    if (imgId) {
      await db.image.delete(imgId);
    }
  });
};

/**
 * Saves current unsaved form inputs as a draft.
 */
export const saveDraft = async (formState) => {
  return db.drafts.put({
    formId: 'inspection-form',
    data: formState,
    updatedAt: new Date().toISOString()
  });
};

/**
 * Loads the saved inspection form draft.
 */
export const loadDraft = async () => {
  const draft = await db.drafts.get('inspection-form');
  return draft ? draft.data : null;
};

/**
 * Clears the saved inspection draft.
 */
export const clearDraft = async () => {
  return db.drafts.delete('inspection-form');
};
